<script setup>
import MonitoringPanel from "@/components/MonitoringPanel.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import { ref, computed, onMounted, onUnmounted, watch, defineEmits } from "vue";
import SearchInput from "@/components/SearchInput.vue";
import FiltersList from "@/components/FiltersList.vue";
import PushList from "@/components/PushList.vue";
import OrderList from "@/components/OrderList.vue";
import FilterModal from "@/components/Modals/FilterModal.vue";
import OrderDetailsModal from "@/components/Modals/OrderDetailsModal.vue";
import NotificationFilters from "@/components/NotificationFilters.vue";
import { useTabsStore } from "@/store/tabs";
import { useOrdersStore } from "@/store/orders";
import { usePushesStore } from "@/store/pushes";
import { useFiltersStore } from "@/store/filters";
import {debounce} from "lodash-es";
import {storeToRefs} from "pinia";
const pushesStore = usePushesStore();
const ordersStore = useOrdersStore();
const filtersStore = useFiltersStore();
const tabsStore = useTabsStore();
const selectedCategory = computed({
  get: () => tabsStore.activeTab,
  set: (value) => tabsStore.setActiveTab(value),
});
const isCollapsed = ref(false);
const selectedOption = ref({});
const showTooltip = ref(false);
const showTooltipFilters = ref(false);
const showTooltipReset = ref(false);
const order = ref(null);
const isOpen = ref(false);
const orderListRef = ref(null);
const pushListRef = ref(null);
const lastVisibilityState = ref(document.visibilityState);

const ordersFiltersPending = ref(false);
const ordersFiltersLoading = ref(false);

const {
  isInitialLoad,
  isRefreshing,
} = storeToRefs(pushesStore);

// Создаем computed свойство для получения count
const orderCount = computed(() => {
  return orderListRef.value?.count || 0;
});
const pushCount = computed(() => {
  return pushListRef.value?.count || 0;
});
const isFilterModalOpen = ref(false);
const currentFilterTitle = ref("");
const currentSelectedValue = ref("");
const currentFilterButtonId = ref(undefined);
const filtersListRef = ref(null);
const filtersKey = ref(0);
const orderSearchQuery = ref("");
const pushSearchQuery = ref("");
const showMoreFilters = ref(false);
const isMultipleSelection = ref(false);


const getRecipientsByFilterTitle = (filterData) => {
  if (!filterData || !filterData.title) return [];

  const titleStr = filterData.title;

  switch (titleStr) {
    case "Клиенты":
      return filtersStore.clients.map((client) => ({
        value: client.id,
        label: client.name,
      }));
    case "Завод":
      return filtersStore.warehouses.map((plant) => ({
        value: plant.id,
        label: plant.name,
      }));
    default:
      return [];
  }
};

const currentFilterRecipients = computed(() => {
  return getRecipientsByFilterTitle({
    title: currentFilterTitle.value,
    buttonId: currentFilterButtonId.value,
  });
});

const handleSearch = (query) => {
  switch (selectedCategory.value) {
    case "orders":
      ordersStore.setSearchFilter(query);
      break;
    case "notifications":
      pushesStore.searchPushes(query);
      break;
    default:
      console.log("Неизвестная категория для поиска");
  }
};

const close = () => {
  isOpen.value = false;
  isFilterModalOpen.value = false;
  currentFilterTitle.value = "";
  currentFilterButtonId.value = undefined;
};

const handleClear = () => {
  switch (selectedCategory.value) {
    case "orders":
      orderSearchQuery.value = "";
      ordersStore.setSearchFilter("");
      break;
    case "notifications":
      pushSearchQuery.value = "";
      pushesStore.searchPushes("");
      break;
  }
};
const titleText = computed(() => {
  switch (selectedCategory.value) {
    case "orders":
      return "Заказы";
    case "notifications":
      return "Push";
    default:
      return "";
  }
});

const count = computed(() => {
  switch (selectedCategory.value) {
    case "orders":
      return orderCount.value;
    case "notifications":
      return pushCount.value;
    default:
      return "";
  }
});
const moreFilters = () => {
  showMoreFilters.value = !showMoreFilters.value;
};

const showFilterModal = (data) => {
  if (
    currentFilterButtonId.value === data.buttonId &&
    isFilterModalOpen.value
  ) {
    // Если нажат тот же фильтр и модальное окно уже открыто, закрываем его
    isFilterModalOpen.value = false;
    currentFilterTitle.value = "";
    currentFilterButtonId.value = undefined;
  } else {
    // В противном случае открываем модальное окно с новыми данными
    currentFilterTitle.value = data.title;
    currentFilterButtonId.value = data.buttonId;
    isMultipleSelection.value = data.title === "Номенклатура";
    isFilterModalOpen.value = true;
  }
};

const showMore = (item) => {
  isOpen.value = true;
  order.value = item;
};

const selectRecipent = (recipient, buttonId) => {
  // Обновляем текущее выбранное значение
  currentSelectedValue.value = recipient.value;

  if (buttonId) {
    switch (buttonId) {
      case 1: // Покупатели
        ordersStore.setClientsFilter([recipient.value]);
        break;
      case 2: // Грузополучатель
        ordersStore.setWarehousesFilter([recipient.value]);
        break;
    }

    // Обновляем текст кнопки
    if (filtersListRef.value) {
      filtersListRef.value.updateButtonText(buttonId, recipient.label);
    }

  }
  if (!isMultipleSelection.value) {
    isFilterModalOpen.value = false;
  }
};

const handleClearFilter = (buttonId) => {
  // Reset the current selected value in the FilterModal
  currentSelectedValue.value = "";
  if (currentFilterButtonId.value === buttonId && isFilterModalOpen.value) {
    isFilterModalOpen.value = false;
    currentFilterTitle.value = "";
    currentFilterButtonId.value = undefined;
  }
  // Обновляем текст кнопки
  if (filtersListRef.value) {
    filtersListRef.value.updateButtonText(buttonId, '');
  }
};

const handleFilterSelection = (recipient, buttonId) => {
  // Обработка выбора из модального окна фильтра
  if (buttonId !== undefined) {
    // Если это множественный выбор (для Номенклатуры)
    if (isMultipleSelection.value) {
      // recipient будет объектом с полями value (массив значений) и label (строка с метками)
      selectRecipent(recipient, buttonId);
    } else {
      // Для одиночного выбора
      selectRecipent(recipient, buttonId);
    }
  }
  if (!isMultipleSelection.value) {
    isFilterModalOpen.value = false;
  }
};

const emit = defineEmits(['collapsed-change'])

const handleCategoryChange = (category) => {
  if (category === "dashboard") {
    isCollapsed.value = !isCollapsed.value;
    if (!isCollapsed.value) {
      tabsStore.setActiveTab("orders");
    }
  } else {
    tabsStore.setActiveTab(category);
    isCollapsed.value = false;
  }
  emit("collapsed-change", isCollapsed.value);
};

const initDashboard = () => {
  isCollapsed.value = false;
  emit("collapsed-change", isCollapsed.value);
  tabsStore.setActiveTab("orders");
};
const resetFilters = () => {
  filtersKey.value += 1;
  showMoreFilters.value = false;
  isFilterModalOpen.value = false;
  currentSelectedValue.value = "";
  switch (selectedCategory.value) {
    case "orders":
      ordersStore.clearAllFilters();
      orderSearchQuery.value = "";
      break;
    case "notifications":
      pushesStore.clearAllFilters();
      pushSearchQuery.value = "";
      break;
  }
};

const fetchPushes = async (forceRefresh = false) => {
  if (isRefreshing.value && !forceRefresh) return;

  isRefreshing.value = true;
  try {
    await pushesStore.fetchPushes();
    count.value = pushesStore.pushesCount;
    isInitialLoad.value = false;
  } catch (error) {
    console.error("Error fetching notifications:", error);
  } finally {
    isRefreshing.value = false;
  }
};

const fetchOrders = async () => {
  return ordersStore.fetchOrders();
};

const handleVisibilityChange = debounce(async () => {
  if (
      document.visibilityState === "visible" &&
      lastVisibilityState.value === "hidden"
  ) {
    await fetchPushes(true);
    if (!ordersFiltersBusy.value) await fetchOrders();
  }
  lastVisibilityState.value = document.visibilityState;
}, 300);

onMounted(async () => {
  initDashboard();
  filtersStore.fetchFilters();

  fetchPushes();
  fetchOrders();

  document.addEventListener("visibilitychange", handleVisibilityChange);

  const updateInterval = setInterval(() => {
    if (document.visibilityState === "visible") {
      if (!isRefreshing.value) fetchPushes(false);
      if (!ordersFiltersBusy.value) {
        fetchOrders();
      }
    }
  }, 30000);

  window._pushesOrdersUpdateInterval = updateInterval;
});

const debouncedFetchOrdersByFilters = debounce(async (seq) => {
  ordersFiltersLoading.value = true;
  try {
    const res = await fetchOrders();
    const status = res?.status;
    if (seq === filtersSeq) {
      if (status === "ok" || status === "error") {
       ordersFiltersPending.value = false;
      }
    }
  } finally {
    ordersFiltersLoading.value = false;
  }
}, 300);

const resetOrdersPage = () => {
  if (orderListRef.value?.resetPage) orderListRef.value.resetPage();
  else if (orderListRef.value) orderListRef.value.currentPage = 1;
};

const ordersFiltersBusy = computed(
    () => ordersFiltersPending.value || ordersFiltersLoading.value
);
let filtersSeq = 0;

watch(
    () => ordersStore.filters,
    () => {
      filtersSeq += 1;
      ordersFiltersPending.value = true;
      resetOrdersPage();
      debouncedFetchOrdersByFilters(filtersSeq);
    },
    { deep: true }
);

onUnmounted(() => {
  // Очищаем интервал обновления
  if (window._mapUpdateInterval) {
    clearInterval(window._mapUpdateInterval);
    delete window._mapUpdateInterval;
  }

  if (window._pushesOrdersUpdateInterval) {
    clearInterval(window._pushesOrdersUpdateInterval);
    delete window._pushesOrdersUpdateInterval;
  }
  document.removeEventListener("visibilitychange", handleVisibilityChange);

  // очищаем все фильтры
  pushesStore.clearAllFilters();
  ordersStore.clearAllFilters();
  ordersStore.resetOrders();
});
</script>

<template>
  <div class="monitoring-container">
    <div class="monitoring-dashboard" :class="{ collapsed: isCollapsed }">
      <MonitoringPanel
        :is-collapsed="isCollapsed"
        :selected-category="selectedCategory"
        @category-change="handleCategoryChange"
      />
      <div class="divider"></div>
      <div v-show="!isCollapsed" class="monitoring-dashboard__content">
        <div class="dashboard-header">
            <h2 class="orders-title">
              {{ titleText }}
              <span :class="{ count: true }">{{ count }}</span>
            </h2>
            <!-- индикатор применения фильтров -->
            <div v-if="selectedCategory === 'orders' && ordersFiltersBusy" class="header-indicator">
              <LoadingSpinner small class="header-spinner" />
              <span class="header-hint">Фильтруем заказы…</span>
            </div>
            <!-- <div
              class="filters_btn"
              v-if="selectedCategory === 'notifications'"
            >
              <svg
                width="21"
                height="20"
                viewBox="0 0 21 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.9472 15.0548L17.1255 16.2332L14.4588 18.8998C14.0722 19.285 13.5487 19.5013 13.003 19.5013C12.4572 19.5013 11.9337 19.285 11.5472 18.8998L8.8805 16.2332L10.0588 15.0548L12.168 17.1665V10.3332H13.8347V17.1665L15.9472 15.0548ZM15.3263 5.50732C14.9545 3.91646 14.0115 2.51751 12.6762 1.57607C11.341 0.63464 9.70659 0.216273 8.08326 0.400401C6.45993 0.58453 4.96072 1.35833 3.87028 2.5749C2.77983 3.79147 2.17407 5.36609 2.168 6.99982C2.16718 8.06516 2.42452 9.11483 2.918 10.059C2.01894 10.5408 1.30666 11.3092 0.894346 12.2422C0.482029 13.1751 0.393318 14.2191 0.642307 15.2083C0.891296 16.1974 1.4637 17.075 2.26858 17.7016C3.07347 18.3281 4.06465 18.6678 5.08466 18.6665H8.953L7.28633 16.9998H5.08466C4.37502 17.0019 3.68907 16.7446 3.15593 16.2762C2.62279 15.8079 2.27923 15.1608 2.18993 14.4568C2.10062 13.7528 2.27172 13.0404 2.67103 12.4537C3.07033 11.8671 3.6703 11.4466 4.358 11.2715L5.5555 10.9623L4.81966 9.96899C4.17895 9.11179 3.83334 8.07001 3.83466 6.99982C3.84556 5.73911 4.33231 4.52905 5.19745 3.61197C6.0626 2.69489 7.24225 2.13849 8.50019 2.0542C9.75813 1.96991 11.0015 2.36395 11.9812 3.15741C12.961 3.95087 13.6048 5.08517 13.7838 6.33315L13.8672 6.96482L14.4997 7.04815C15.3388 7.15946 16.1359 7.48241 16.8158 7.98663C17.4958 8.49085 18.0363 9.15977 18.3865 9.93044C18.7367 10.7011 18.8851 11.5482 18.8177 12.392C18.7503 13.2358 18.4694 14.0487 18.0013 14.754L19.193 15.9457C19.8509 15.0577 20.2771 14.0198 20.4329 12.9257C20.5887 11.8317 20.4693 10.716 20.0854 9.67975C19.7015 8.64346 19.0653 7.71924 18.2344 6.99071C17.4034 6.26219 16.4039 5.75235 15.3263 5.50732Z"
                  fill="white"
                />
              </svg>
            </div> -->
          </div>
        <div class="search-bar">
          <div class="search-tips">
            <SearchInput
              :modelValue="
                selectedCategory === 'orders'
                  ? orderSearchQuery
                  : pushSearchQuery
              "
              @update:modelValue="
                (value) => {
                  if (selectedCategory === 'orders') orderSearchQuery = value;
                  else if (selectedCategory === 'notifications')
                    pushSearchQuery = value;
                }
              "
              placeholder="Поиск"
              @search="
                handleSearch(
                  selectedCategory === 'orders'
                    ? orderSearchQuery
                    : pushSearchQuery
                )
              "
              @clear="handleClear"
              @mouseenter="showTooltip = true"
              @mouseleave="showTooltip = false"
              width="100%"
              height="36px"
            />
            <div
              class="custom-tooltip"
              v-show="showTooltip && selectedCategory === 'orders'"
            >
              <div class="tooltip-content">
                Поиск по номеру заказа / госномеру ТС / перевозчику / клиенту
              </div>
            </div>
          </div>

          <div
            class="search-bar_item"
            @mouseenter="showTooltipFilters = true"
            @mouseleave="showTooltipFilters = false"
            @click.stop="moreFilters"
          >
            <div
              class="custom-tooltip tooltip-right"
              v-show="showTooltipFilters"
            >
              <div class="tooltip-content">Показать фильтры</div>
            </div>
            <svg
              width="21"
              height="21"
              viewBox="0 0 21 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_562_3793)">
                <path
                  d="M12.9997 20.5L7.99967 16.75V12.4833L1.33301 4.98333V3C1.33301 2.33696 1.5964 1.70107 2.06524 1.23223C2.53408 0.763392 3.16997 0.5 3.83301 0.5L17.1663 0.5C17.8294 0.5 18.4653 0.763392 18.9341 1.23223C19.4029 1.70107 19.6663 2.33696 19.6663 3V4.98333L12.9997 12.4833V20.5ZM9.66634 15.9167L11.333 17.1667V11.85L17.9997 4.35V3C17.9997 2.77899 17.9119 2.56702 17.7556 2.41074C17.5993 2.25446 17.3874 2.16667 17.1663 2.16667H3.83301C3.61199 2.16667 3.40003 2.25446 3.24375 2.41074C3.08747 2.56702 2.99967 2.77899 2.99967 3V4.35L9.66634 11.85V15.9167Z"
                  fill="#4D5460"
                />
              </g>
              <defs>
                <clipPath id="clip0_562_3793">
                  <rect
                    width="20"
                    height="20"
                    fill="white"
                    transform="translate(0.5 0.5)"
                  />
                </clipPath>
              </defs>
            </svg>
          </div>
          <div
            class="search-bar_item"
            @mouseenter="showTooltipReset = true"
            @mouseleave="showTooltipReset = false"
            @click.stop="resetFilters"
          >
            <div
              class="custom-tooltip tooltip-right"
              v-show="showTooltipReset"
            >
              <div class="tooltip-content">Сбросить фильтры</div>
            </div>
            <svg
              width="11"
              height="11"
              viewBox="0 0 11 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.207 2.20703L6.91406 5.5L10.207 8.79297L8.79297 10.207L5.5 6.91406L2.20703 10.207L0.792969 8.79297L4.08594 5.5L0.792969 2.20703L2.20703 0.792969L5.5 4.08594L8.79297 0.792969L10.207 2.20703Z"
                fill="#EB5E5E"
              />
            </svg>
          </div>
        </div>
        <FiltersList
          ref="filtersListRef"
          :select-option="selectedOption"
          @show-modal="showFilterModal"
          @clear-filter="handleClearFilter"
          v-show="showMoreFilters && selectedCategory === 'orders'"
          :key="`filters-list-${filtersKey}`"
        />
        <NotificationFilters
          v-show="showMoreFilters && selectedCategory === 'notifications'"
          :key="`notification-filters-messages-${filtersKey}`"
          @show-modal="showFilterModal"
          @clear-filter="handleClearFilter"
        />

        <OrderList
          ref="orderListRef"
          v-if="!isCollapsed && selectedCategory === 'orders'"
          :filters-open="showMoreFilters"
          @show-more="showMore"
        />
        <PushList
          ref="pushListRef"
          v-if="!isCollapsed && selectedCategory === 'notifications'"
          :filters-open="showMoreFilters"
        />
      </div>
    </div>
    <FilterModal
      :isOpen="isFilterModalOpen"
      :title="{ title: currentFilterTitle, buttonId: currentFilterButtonId }"
      :recipients="currentFilterRecipients"
      :selectedValue="currentSelectedValue"
      :multiple="isMultipleSelection"
      @close="close"
      @select="handleFilterSelection"
    />
  </div>

  <OrderDetailsModal :is-open="isOpen" :order="order" @close="close" />
</template>

<style scoped lang="scss">
.monitoring-container {
  position: relative;
  display: flex;
}

.monitoring-dashboard {
  // background-color: var(--body-color);
  background-color: #ffffff;
  display: flex;
  align-items: flex-start;
  column-gap: 16px;
  border-radius: 16px;
  padding: 16px;
  width: 100%;
  max-width: 440px;
  height: calc(100vh - 96px);
  overflow: hidden;
  transition: max-width 0.3s ease;

  &.collapsed {
    max-width: 78px;
  }
}

.search-bar_item {
  position: relative;
  display: block;
  cursor: pointer;
}

:deep(.filter-modal-container) {
  position: fixed;
  top: 88px;
  width: 100%;
  max-width: 317px;
  left: calc(440px + 15px);
  height: calc(100vh - 112px);
  z-index: 1000;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.divider {
  height: 100%;
  width: 1px;
  flex-shrink: 0;
  background-color: #4d546012;
  transition: opacity 0.3s ease-in-out;
}

.collapsed .divider {
  opacity: 0;
}

.filters_btn {
  background-color: #005291;
  border-radius: 6px;
  color: white;
  height: 35px;
  width: 35px;
  padding: 7px;
  cursor: pointer;

  svg {
    fill: white;
    display: flex;
  }
}

.orders-title {
  font-size: 24px;
  margin: 0px;
  line-height: 25px;
  font-weight: 600;
  color: #4d5460;
}

.orders-title span {
  font-weight: 400;
  color: #a6a9af;
}
.custom-tooltip {
  position: absolute;
  left: 0;
  right: 0; /* Change from 'auto' to '0' to ensure full width */
  top: 100%;
  transform: none;
  background-color: #000000;
  color: #ffffff;
  padding: 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 400;
  font-family: Open Sans, sans-serif;
  z-index: 10;
  width: 100%;
  margin-top: 5px;
  white-space: pre-line;
  word-wrap: break-word;
  line-height: 1;
  text-align: center;
  box-sizing: border-box;

  &:after {
    content: "";
    position: absolute;
    bottom: 100%;
    left: auto;
    transform: none;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid #000000;
    border-top: none;
  }
}

.tooltip-right {
  position: absolute;
  left: auto;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  background-color: #000000;
  color: #ffffff;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 400;
  font-family: Open Sans, sans-serif;
  z-index: 10;
  width: max-content;
  max-width: 200px;
  margin-right: 10px;
  white-space: pre-line;
  word-wrap: break-word;
  line-height: 1.4;
  text-align: left;

  &:after {
    content: "";
    position: absolute;
    top: 50%;
    left: 100%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 6px solid #000000;
  }
}

.tooltip-content {
  text-align: center;
  width: 100%;
}

.search-tips {
  position: relative;
  display: block;
  cursor: pointer;
}

.search-bar {
  display: flex;
  margin-bottom: 16px;
  column-gap: 8px;
  align-items: center;
  max-width: 320px;

  &_item {
    // background: var(--card-bg);
    background: #f6f6f6;
    border-radius: 6px;
    height: 35px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    width: 35px;
  }
}

input[type="text"] {
  flex-grow: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-right: 8px;
}

.search-button,
.filter-button {
  background: none;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
}

.search-icon,
.filter-icon {
  font-style: normal;
}

.header-indicator {
  position: relative;
  display: flex;
  align-items: center;
  height: 24px;
}

.header-spinner {
  width: 24px;
  height: 24px;
}

.header-hint {
  margin-left: 28px;
  font-size: 14px;
  color: #a6a9af;
}
</style>
